import React, { useEffect, useMemo, useRef } from 'react';
import Box from '@material-ui/core/Box';
import {
  BedSlot,
  ORItem,
  PACUv2SectionSeparator,
  SelectNurseDialog,
  SelectPatientDialog,
} from '@/components/pages/kiosk/PACUv2/index';
import { useSubscription } from '@apollo/client';
import { listOperationRoomsSubscription } from '@/graph/rooms';
import { PacuV2State } from '@/components/pages/kiosk/PACUv2/PacuV2State';
import { listOrPacuPatients } from '@/graph/patients';
import { makeStyles } from '@material-ui/core/styles';
import { patientEnteredTime } from '@/components/pages/kiosk/tablet/utils';
import { useScreenConfiguration } from '@/contexts/ScreenConfigurationContext';
import { useWebSocketReconnect } from '@/hooks/useWebSocketReconnect';

interface PACUv2Props {
  room: any;
  patients: any[];
  selectPatient: (patient: any) => void;
}

const PACUv2 = ({ room, selectPatient }: PACUv2Props) => {
  // Track WebSocket reconnections for logging and debugging
  const reconnectCount = useWebSocketReconnect();
  const prevReconnectCount = useRef(reconnectCount);

  // Log reconnection events for debugging production issues
  useEffect(() => {
    if (reconnectCount > 0 && reconnectCount !== prevReconnectCount.current) {
      console.log(`[PACUv2] WebSocket reconnected (count: ${reconnectCount}), subscriptions will auto-resubscribe`);
      prevReconnectCount.current = reconnectCount;
    }
  }, [reconnectCount]);

  // shouldResubscribe: true ensures subscription re-establishes after WebSocket reconnection
  const query = useSubscription(listOrPacuPatients, { shouldResubscribe: true });
  const rawPatients = query.data?.patients ?? [];

  const { showPatientTypes, showPhysicians } = useScreenConfiguration();

  const showPatientTypesSet = useMemo(() => {
    if (!showPatientTypes || showPatientTypes.length === 0) return null;
    return new Set(showPatientTypes);
  }, [showPatientTypes]);

  const showPhysiciansSet = useMemo(() => {
    if (!showPhysicians || showPhysicians.length === 0) return null;
    return new Set(showPhysicians.map(id => String(id)));
  }, [showPhysicians]);

  const matchesPhysician = useMemo(
    () => (patient: any) =>
      !showPhysiciansSet || (patient?.physician?.id != null && showPhysiciansSet.has(String(patient.physician.id))),
    [showPhysiciansSet]
  );

  // Filter patients by type and physician if configured
  const patients = useMemo(() => {
    if (!showPatientTypesSet && !showPhysiciansSet) return rawPatients;
    return rawPatients.filter(
      patient => (!showPatientTypesSet || showPatientTypesSet.has(patient.type || '')) && matchesPhysician(patient)
    );
  }, [rawPatients, showPatientTypesSet, showPhysiciansSet, matchesPhysician]);

  const operationRoomsSubscription = useSubscription(listOperationRoomsSubscription, { shouldResubscribe: true });

  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState('Dione');
  const [bedNumber, setBedNumber] = React.useState(1);

  const [patientId, setPatientId] = React.useState<any>(undefined);
  const [roomId, setRoomId] = React.useState<any>(undefined);
  const [assignNurseOpen, setAssignNurseOpen] = React.useState<boolean>(false);

  const handleClickListItem = (bedNumber: number) => {
    setBedNumber(bedNumber);
    setOpen(true);
  };

  const handleAssigned = (patientId, roomId) => {
    const patient = patients.find(p => parseInt(p.id, 10) === parseInt(patientId, 10));

    if (patient && !patient.pacuNurse) {
      setPatientId(patientId);
      setRoomId(roomId);
      setAssignNurseOpen(true);
    }
  };

  const handleAssignNurse = (patientId: any, roomId: any) => {
    setPatientId(patientId);
    setRoomId(roomId);
    setAssignNurseOpen(true);
  };

  const selectedNursePatient = React.useMemo(
    () => patients.find(p => parseInt(p.id, 10) === parseInt(patientId, 10)),
    [patients, patientId]
  );

  const handleClose = (newValue?: string) => {
    setOpen(false);
    setAssignNurseOpen(false);

    if (newValue) {
      setValue(newValue);
    }
  };

  const operationRooms = operationRoomsSubscription.data?.internalMonitor;

  const patientsByPacuBed = useMemo(() => {
    const map = new Map<string, any>();
    for (const patient of patients) {
      if (!patient || patient.id == null) continue;
      const bed = patient.pacuBed;
      if (bed == null) continue;
      map.set(String(bed), patient);
    }
    return map;
  }, [patients]);

  const unassignedPatients = useMemo(
    () =>
      patients.filter(p => {
        const pacuBedNumber = parseInt(p.pacuBed, 10);
        return (
          p.room?.id === room.id && (!isFinite(pacuBedNumber) || pacuBedNumber < 1 || pacuBedNumber > room.capacity)
        );
      }),
    [patients, room.capacity, room.id]
  );

  return (
    <PacuV2State>
      {setState => (
        <Box className={classes.root}>
          <Box className={classes.grid}>
            {operationRooms && (
              <Box className={classes.orList}>
                {operationRooms.map(operationRoom => {
                  // Disable OR item if the patient doesn't match the configured type or physician filters
                  const patient = operationRoom.patient;
                  const isDisabled = Boolean(
                    patient &&
                      ((showPatientTypesSet && !showPatientTypesSet.has(patient.type || '')) ||
                        !matchesPhysician(patient))
                  );

                  return (
                    <ORItem
                      key={operationRoom.id}
                      orName={operationRoom.name}
                      patientId={operationRoom.patient?.id}
                      patientName={operationRoom.patient?.name}
                      patientPacuBed={operationRoom.patient?.pacuBed}
                      surgeonName={operationRoom.patient?.physician?.name}
                      status={operationRoom.patient?.status}
                      disabled={isDisabled}
                    />
                  );
                })}
              </Box>
            )}

            <PACUv2SectionSeparator roomId={room?.id} />

            <Box className={classes.bedSlots}>
              {[...Array(room.capacity)].map((_, index) => {
                const patient = patientsByPacuBed.get(String(index + 1));

                return (
                  <BedSlot
                    key={index}
                    patientId={patient?.id}
                    patientName={patient?.name}
                    pacuNurse={patient?.pacuNurse}
                    surgeonName={patient?.physician?.name}
                    procedure={patient?.procedureType?.name}
                    bedNumber={index + 1}
                    isAssigned={Boolean(patient)}
                    onClick={() => {
                      if (patient) {
                        selectPatient(patient.id);
                      } else {
                        handleClickListItem(index + 1);
                      }
                    }}
                    onAssignNurse={handleAssignNurse}
                    onAssigned={handleAssigned}
                    roomId={room?.id}
                    inside={room?.id === patient?.room?.id}
                    room={patient?.room}
                    timeEntered={patient && room && patientEnteredTime(patient, room)}
                    transferredToChair={patient?.transferredToChair}
                    transferredToPT={patient?.transferredToPT}
                    ptComplete={patient?.ptComplete}
                  />
                );
              })}

              {unassignedPatients.length > 0 &&
                unassignedPatients.map((patient, index) => (
                  <BedSlot
                    key={patient?.id ?? index}
                    patientId={patient?.id}
                    patientName={patient?.name}
                    pacuNurse={patient?.pacuNurse}
                    surgeonName={patient?.physician?.name}
                    procedure={patient?.procedureType?.name}
                    isAssigned={Boolean(patient)}
                    onClick={() =>
                      setState({ type: 'AssignBed', patientId: patient.id, patientPacuBed: patient?.pacuBed })
                    }
                    onAssignNurse={handleAssignNurse}
                    onAssigned={handleAssigned}
                    roomId={room?.id}
                    inside={room?.id === patient?.room?.id}
                    room={room}
                  />
                ))}
            </Box>
          </Box>

          <SelectPatientDialog
            id="select-patient-dialog"
            keepMounted
            open={open}
            onAssigned={handleAssigned}
            onClose={handleClose}
            value={value}
            bedNumber={bedNumber}
            roomId={room?.id}
          />

          <SelectNurseDialog
            id="select-nurse-dialog"
            keepMounted
            open={assignNurseOpen}
            onClose={handleClose}
            patientId={patientId}
            roomId={roomId}
            assignedNurse={selectedNursePatient?.pacuNurse}
          />
        </Box>
      )}
    </PacuV2State>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  grid: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  orList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: theme.spacing(1),
  },
  bedSlots: {
    flex: 1,
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(200px, 1fr)',
    gridTemplateRows: 'repeat(auto-fill, minmax(76px, 1fr))',
    gap: theme.spacing(1),
  },
}));

export default PACUv2;
